import { useContext, useEffect, useState } from "react";
import { NormalTableRow, SettingTable, SmallContentTableRow, SmallTextTableRow, settingUseStyles } from "../Settings";
import TextCell from "../components/TextCell";
import { Link, Paper, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, styled, useMediaQuery } from "@mui/material";
import { useSnackbar } from "notistack";
import { APIsContext } from "../../../Store/APIs/context";
import { NavigationContext } from "../../../Store/Navigation/context";
import { setDeveloperOptions } from "../../../Store/Navigation/reducer";
import { AuthenticationRole } from "../../../Store/Authentication/types";

const NameTableCell = styled(TableCell)(({theme}) => ({
    paddingLeft: "3rem",
    [theme.breakpoints.down("sm")]: {
        paddingLeft: "1rem"
    }
}));

interface IAboutProps {
    role: AuthenticationRole
}

const About: React.FC<IAboutProps> = (props) => {

    const classes = settingUseStyles();
    const { enqueueSnackbar } = useSnackbar();
    const APIstate = useContext(APIsContext).state
    const {state, dispatch} = useContext(NavigationContext)
    const [managementState, setManagementState] = useState<{[key: string]: object}>();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const [secretClicks, setSecretClicks] = useState(0)

    useEffect(() => {
        const fetchData = async () => {
            if (props.role === AuthenticationRole.ROLE_ADMIN) {
                APIstate.ManagementApi.getManagementInfo().then(res => {
                    setManagementState(res.data)
                }).catch((err: any) => {
                    enqueueSnackbar(err.message, {variant: "error"})
                })
            }
            if (props.role === AuthenticationRole.ROLE_ADJUDICATOR) {
                APIstate.ManagementApi.getAdjudicatorManagementInfo().then(res => {
                    setManagementState(res.data)
                }).catch((err: any) => {
                    enqueueSnackbar(err.message, {variant: "error"})
                })
            }
        }
        fetchData()
    }, [enqueueSnackbar, APIstate, props.role])

    useEffect(() => {
        if (secretClicks > 6 && !state.developerOptions) {
            dispatch(setDeveloperOptions(true))
            enqueueSnackbar("Developer options enabled", {variant: "info"})
        }
    }, [secretClicks, dispatch, enqueueSnackbar, state.developerOptions])

    const secretClick = () => {
        setSecretClicks(secretClicks + 1)
        setTimeout(() => {
            setSecretClicks(0)
        }, 5000)
    }

    const getDate = (date: any): string => {
        if (date && date as unknown as number > 0) {
            return new Date(managementState?.buildDate as unknown as number).toISOString().split('T')[0]
        } else {
            return "none"
        }
    }

    const getColSpan = (): number => {
        if (isMobile)
            return 2
        if (isSmall)
            return 2;
        return 1
    }

    const showTableRow = (name: string, value: string, click?: () => void) => {
        if (isMobile) {
            return (
                <TableRow onClick={click? click : undefined}>
                    <NameTableCell width={"50%"} colSpan={getColSpan()}>
                        {name}
                    </NameTableCell>
                    <TableCell colSpan={3}>
                        {value}
                    </TableCell>
                </TableRow>
            );
        } if (isSmall) {
            return (
                <TableRow onClick={click? click : undefined}>
                    <NameTableCell width={"50%"} colSpan={getColSpan()}>
                        {name}
                    </NameTableCell>
                    <TableCell colSpan={3}>
                        {value}
                    </TableCell>
                </TableRow>
            );
        } else {
            return (
                <TableRow onClick={click? click : undefined}>
                    <NameTableCell width={"25%"} colSpan={getColSpan()}>
                        {name}
                    </NameTableCell>
                    <TableCell colSpan={3}>
                        {value}
                    </TableCell>
                </TableRow>
            );
        }
    }

    const contactUsRow = () => {
        if (isMobile) {
            return (
                <>
                <SmallTextTableRow>
                    <TextCell colspan={5} title="Need help or want to report an issue?" subtitle="Contact us"/>
                </SmallTextTableRow>
                <SmallContentTableRow>
                    <TableCell align="center" colSpan={3}>
                        <Link style={{textDecoration: "none", color: "#477eb8", fontSize: "0.9rem", fontWeight: 500}} href="mailto:megamatcherid@neurotechnology.com">megamatcherid@neurotechnology.com</Link>
                    </TableCell>
                </SmallContentTableRow>
                </>
            )
        } else {
            return (
                <NormalTableRow>
                    <TextCell colspan={3} title="Need help or want to report an issue?" subtitle="Contact us"/>
                    <TableCell>
                        <Link style={{textDecoration: "none", color: "#477eb8", fontSize: "0.9rem", fontWeight: 500}} href="mailto:megamatcherid@neurotechnology.com">megamatcherid@neurotechnology.com</Link>
                    </TableCell>
                </NormalTableRow>
            )
        }
    }

    return(
        <TableContainer component={Paper} className={classes.container}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell colspan={5} head title="Mega Matcher Id Web demo" subtitle=""/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {contactUsRow()}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Adjudicator decisions needed", (managementState?.adjudicationDecisionRequiresTotal ?? "none").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Accepted decisions needed", (managementState?.adjudicationDecisionRequiresAccepted ?? "none").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Face age adjudication", (managementState?.adjudicationFaceAgeEnabled ?? "false").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Face age lower bound", (managementState?.adjudicationFaceAgeLowerBound ?? "none").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Face age upper bound", (managementState?.adjudicationFaceAgeUpperBound ?? "none").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Face liveness adjudication", (managementState?.adjudicationFaceLivenessEnabled ?? "false").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Face liveness threshold", (managementState?.adjudicationFaceLivenessThreshold ?? "none").toString())}
                    {props.role === AuthenticationRole.ROLE_ADJUDICATOR && showTableRow("Face liveness range", (managementState?.adjudicationFaceLivenessRange ?? "none").toString())}
                    {showTableRow("Version", (managementState?.version? managementState?.version : "none").toString(), props.role === AuthenticationRole.ROLE_ADMIN? secretClick : undefined)}
                    {showTableRow("Revision", (managementState?.revision? managementState?.revision : "none").toString().substring(0, 8))}
                    {showTableRow("Revision date", (managementState?.buildDate? getDate(managementState?.buildDate) : "none").toString())}
                </TableBody>
            </SettingTable>
        </TableContainer>
    );
}

export default About;
